import { polygonBounds, strokeOutline } from "../stroke/outline";
import { StrokeBuilder, type WorldSample } from "../stroke/builder";
import type { InputSample } from "../input/pointer";
import type { Polygon, StrokePoint } from "../stroke/types";
import { outlinePolygon, transformPolygon } from "../physics/shapes";
import { bodyFromOutline } from "../physics/stroke-matter";
import { symmetryTransforms } from "../symmetry/symmetry";
import type { InkItem } from "../scene/types";
import type { Mat2d } from "../core/mat2d";
import { uid } from "../core/rng";
import { placePullShape, pullShape, randomPullShape } from "./pull-shapes";
import type { Tool, ToolContext, WetStroke } from "./types";

/**
 * Pincel: los tres modos de Alchemy sobre un motor moderno, mas un borrador
 * vectorial propio.
 *
 * - stroke: silueta de ancho variable a partir de la dinamica activa.
 * - fill: la trayectoria se cierra y se rellena; el ancho no importa.
 * - pull: se estira una forma procedural entre el punto inicial y el actual.
 * - erase: borrador inteligente (ver mas abajo).
 *
 * El trazo se repinta como "humedo" en su propia capa en cada lote de muestras
 * y solo se consolida al levantar el lapiz. La prediccion se pinta pero jamas
 * se consolida: si el lapiz cambia de rumbo, el tramo predicho desaparece sin
 * dejar rastro.
 *
 * El borrador (`mode === "erase"`) tiene cuatro sub-modos (`brush.eraseMode`):
 * - brush: trazo de borrado de ancho constante que recorta la tinta debajo
 *   (item con `erase`, compuesto con destination-out por el renderer).
 * - shape: usa la forma activa de Materia como huella y recorta ese area.
 * - object: tocar un trazo lo elimina entero.
 * - color: como object, pero solo los trazos del color del primero que se toca.
 * Ademas `eraseFade` lo vuelve suave (opacidad = fuerza) y `eraseMatter` hace
 * que el mismo gesto borre cuerpos de materia que toque.
 */
export class BrushTool implements Tool {
  readonly id = "brush" as const;
  readonly cursor = "none";
  readonly showCursorRing = true;

  private builder: StrokeBuilder | null = null;
  private active = false;
  private eraser = false;
  // Al pintar con "usar como borrador" activo (toggle del panel / tecla Alt), el
  // gesto se invierte a borrado sin cambiar de modo (p. ej. la forma de relleno
  // recorta la tinta). El modo Borrador (4) ya borra por sí mismo.
  private invert = false;
  private shape: Polygon | null = null;
  private startX = 0;
  private startY = 0;
  private lastX = 0;
  private lastY = 0;
  // Borrado: si algo se elimino (para decidir commit/abort) y el color de
  // referencia del sub-modo "color" (fijado al tocar el primer trazo).
  private erasedAny = false;
  private eraseRef = "";
  // Trazo ráster en vivo: cuando el pincel está en modo ráster, el trazo se
  // estampa MIENTRAS se mueve el lápiz (no al soltar). `rasterSent` cuenta
  // cuántos puntos consolidados del builder ya se han enviado a estampar.
  private raster = false;
  private rasterSent = 0;
  /** Borrado ráster en curso (modo Borrador con capa de píxeles de destino). */
  private rasterErasing = false;

  private isTargetLocked(ctx: ToolContext): boolean {
    const layer = ctx.doc.activeLayer;
    // Si la activa es imagen bloqueada o ink bloqueada, bloquear pintura.
    if (!layer) return false;
    if (layer.locked) return true;
    // Si inkTarget difiere (activa es matter/group), chequa el ink destino también.
    if (layer.kind !== "ink") {
      const target = ctx.doc.inkTarget();
      return !!target?.locked;
    }
    return false;
  }

  onDown(ctx: ToolContext, s: InputSample): void {
    const w = ctx.toWorld(s);
    if (this.isTargetLocked(ctx)) {
      ctx.status("Capa bloqueada");
      return;
    }
    ctx.history.begin();
    this.active = true;
    this.eraser = s.eraser;
    // Los modos de pintura se invierten a borrado si "usar como borrador" está
    // activo. Materia se controla con el toggle del pincel, no con otro modo.
    this.invert = ctx.brush.mode !== "erase" && !ctx.brush.asMatter && ctx.brush.invertErase;
    this.startX = w.x;
    this.startY = w.y;
    this.lastX = w.x;
    this.lastY = w.y;
    this.erasedAny = false;
    this.raster = false;
    this.rasterErasing = false;

    if (ctx.brush.mode === "erase") {
      // Con el pincel ráster activo, la goma abre el bitmap de la capa en vez de
      // recortar tinta vectorial (sub-modos pincel y forma; objeto/color no
      // aplican a píxeles).
      if (ctx.rasterBrushActive) this.beginRasterErase(ctx, s);
      else this.beginErase(ctx, s, w.x, w.y);
      return;
    }

    if (ctx.brush.mode === "pull") {
      this.shape =
        ctx.pullFamily === "random"
          ? randomPullShape(ctx.rng)
          : pullShape(ctx.pullFamily, ctx.rng);
      this.paintWet(ctx);
      return;
    }

    this.builder = new StrokeBuilder(ctx.brush);
    this.builder.setZoom(ctx.camera.zoom);
    this.builder.begin(this.worldSample(ctx, s));
    // Pincel ráster en modo Trazo: se estampa en vivo sobre la capa de píxeles
    // (con `invert` → borra). El Relleno, en cambio, muestra la vista previa
    // vectorial y rellena el área al soltar (como el relleno vectorial).
    if (ctx.rasterBrushActive && ctx.brush.mode !== "fill") {
      this.raster = true;
      this.rasterSent = 0;
      ctx.rasterStrokeBegin(this.invert);
      this.flushRaster(ctx);
      return;
    }
    this.paintWet(ctx);
  }

  onMove(ctx: ToolContext, samples: InputSample[], predicted: InputSample[]): void {
    if (!this.active) return;

    if (ctx.brush.mode === "erase") {
      if (this.rasterErasing) this.moveRasterErase(ctx, samples);
      else this.moveErase(ctx, samples, predicted);
      return;
    }

    // El toggle "usar como goma" (Alt) puede cambiar a mitad del trazo: se
    // relee para que la vista previa alterne entre pintar y borrar al vuelo.
    // (Aquí el modo ya no puede ser "erase": esa rama salió antes.)
    this.invert = ctx.brush.invertErase;

    if (ctx.brush.mode === "pull") {
      const w = ctx.toWorld(samples[samples.length - 1]);
      this.lastX = w.x;
      this.lastY = w.y;
      this.paintWet(ctx);
      return;
    }

    const b = this.builder;
    if (!b) return;
    b.setZoom(ctx.camera.zoom);
    const added = b.push(samples.map((s) => this.worldSample(ctx, s)));
    if (this.raster) {
      // En vivo: estampar los puntos nuevos. La predicción no se estampa (no se
      // consolida), así que la línea ráster nunca "se adelanta" ni retrocede.
      if (added) this.flushRaster(ctx);
      return;
    }
    if (predicted.length > 0) {
      b.setPredicted(predicted.map((s) => this.worldSample(ctx, s)));
    }
    if (added || predicted.length > 0) this.paintWet(ctx);
  }

  onUp(ctx: ToolContext, s: InputSample): void {
    if (!this.active) return;
    this.active = false;

    if (ctx.brush.mode === "erase") {
      if (this.rasterErasing) this.endRasterErase(ctx, s);
      else this.endErase(ctx, s);
      return;
    }

    // Estado final de "usar como borrador" / "hacer materia".
    this.invert = !ctx.brush.asMatter && ctx.brush.invertErase;

    if (ctx.brush.mode === "pull") {
      const w = ctx.toWorld(s);
      this.lastX = w.x;
      this.lastY = w.y;
      const wet = this.buildWet(ctx, false);
      this.shape = null;
      // "Hacer materia" tambien convierte el arrastre: la forma estirada pasa
      // a ser un cuerpo de silueta libre, igual que el trazo y el relleno.
      if (ctx.brush.asMatter) {
        this.finishMatter(ctx, wet?.polys ?? []);
        return;
      }
      // Acuarela: la misma silueta estirada se vierte en el plano de fluido.
      if (ctx.aquaBrushActive) {
        this.finishAquaArea(ctx, wet?.polys ?? []);
        return;
      }
      // Pincel ráster: la silueta estirada se rellena sobre la capa de píxeles
      // (con `invert` → borra ese área).
      if (ctx.rasterBrushActive) {
        ctx.setWet(null);
        const polys = wet?.polys ?? [];
        if (polys.length) ctx.fillRaster(polys, this.invert);
        ctx.history.abort();
        return;
      }
      this.finish(ctx, wet, this.invert ? "Borrar" : "Forma");
      return;
    }

    const b = this.builder;
    this.builder = null;
    if (!b) {
      if (this.raster) {
        this.raster = false;
        ctx.rasterStrokeEnd();
      }
      ctx.history.abort();
      return;
    }
    b.push([this.worldSample(ctx, s)], true);

    // Pincel ráster: ya se estampó en vivo durante el gesto. Se vuelca el último
    // tramo (con los radios SIN afilar que se usaron en vivo, no los de
    // `finalize`), se cierra el buffer y se descarta el trazo del historial de
    // items (el bitmap es un medio vivo, igual que la acuarela).
    if (this.raster) {
      this.flushRaster(ctx);
      this.raster = false;
      ctx.rasterStrokeEnd();
      ctx.history.abort();
      return;
    }

    const points = b.finalize();

    if (ctx.brush.asMatter) {
      this.finishMatter(ctx, this.wetFromPoints(ctx, points, false)?.polys ?? []);
      return;
    }
    // Pincel ráster en modo Relleno: el área cerrada se rellena sobre la capa de
    // píxeles (con `invert` → borra). El Trazo ya se estampó en vivo arriba.
    if (ctx.rasterBrushActive) {
      ctx.setWet(null);
      const polys = this.wetFromPoints(ctx, points, false)?.polys ?? [];
      if (polys.length) ctx.fillRaster(polys, this.invert);
      ctx.history.abort();
      return;
    }
    // Acuarela: el trazo/relleno (con toda su dinamica ya resuelta) se siembra en
    // el fluido. El Trazo usa su linea central (radios por punto); el Relleno usa
    // el area cerrada. Asi el gesto es identico al vectorial, pero fluye.
    if (ctx.aquaBrushActive) {
      if (ctx.brush.mode === "fill") {
        this.finishAquaArea(ctx, this.wetFromPoints(ctx, points, false)?.polys ?? []);
      } else {
        this.finishAquaStroke(ctx, points);
      }
      return;
    }

    const wet = this.wetFromPoints(ctx, points, false);
    const label = this.invert ? "Borrar" : ctx.brush.mode === "fill" ? "Relleno" : "Trazo";
    this.finish(ctx, wet, label);
  }

  onCancel(ctx: ToolContext): void {
    this.active = false;
    this.builder = null;
    this.shape = null;
    this.invert = false;
    if (this.raster) {
      this.raster = false;
      ctx.rasterStrokeCancel();
    }
    this.rasterErasing = false;
    ctx.setWet(null);
    ctx.history.abort();
  }

  // ----------------------------------------------------------------- borrado

  private beginErase(ctx: ToolContext, s: InputSample, x: number, y: number): void {
    const em = ctx.brush.eraseMode;
    if (ctx.brush.eraseMatter) this.eraseMatterAt(ctx, x, y);

    if (em === "object" || em === "color") {
      this.eraseRef = em === "color" ? this.colorAt(ctx, x, y) : "";
      this.eraseObjectsAt(ctx, x, y);
      return;
    }

    if (em === "brush") {
      // Ancho constante: el borrador no debe adelgazar con la dinamica.
      this.builder = new StrokeBuilder({ ...ctx.brush, dynamics: "constant", minRatio: 1 });
      this.builder.setZoom(ctx.camera.zoom);
      this.builder.begin(this.worldSample(ctx, s));
    }
    // brush o shape: se previsualiza como recorte humedo.
    this.paintWet(ctx);
  }

  private moveErase(ctx: ToolContext, samples: InputSample[], predicted: InputSample[]): void {
    const em = ctx.brush.eraseMode;
    if (ctx.brush.eraseMatter) {
      for (const sm of samples) {
        const w = ctx.toWorld(sm);
        this.eraseMatterAt(ctx, w.x, w.y);
      }
    }

    if (em === "object" || em === "color") {
      for (const sm of samples) {
        const w = ctx.toWorld(sm);
        this.eraseObjectsAt(ctx, w.x, w.y);
      }
      return;
    }

    if (em === "shape") {
      const w = ctx.toWorld(samples[samples.length - 1]);
      this.lastX = w.x;
      this.lastY = w.y;
      this.paintWet(ctx);
      return;
    }

    const b = this.builder;
    if (!b) return;
    b.setZoom(ctx.camera.zoom);
    const added = b.push(samples.map((s) => this.worldSample(ctx, s)));
    if (predicted.length > 0) b.setPredicted(predicted.map((s) => this.worldSample(ctx, s)));
    if (added || predicted.length > 0) this.paintWet(ctx);
  }

  private endErase(ctx: ToolContext, s: InputSample): void {
    const em = ctx.brush.eraseMode;

    if (em === "object" || em === "color") {
      if (this.erasedAny) ctx.history.commit("Borrar");
      else ctx.history.abort();
      return;
    }

    if (em === "shape") {
      const w = ctx.toWorld(s);
      this.lastX = w.x;
      this.lastY = w.y;
      this.finishErase(ctx, this.buildWet(ctx, false));
      return;
    }

    const b = this.builder;
    this.builder = null;
    if (!b) {
      this.finishErase(ctx, null);
      return;
    }
    b.push([this.worldSample(ctx, s)], true);
    const points = b.finalize();
    this.finishErase(ctx, this.wetFromPoints(ctx, points, false));
  }

  /** Consolida un recorte de borrado; respeta lo ya borrado de materia. */
  private finishErase(ctx: ToolContext, wet: WetStroke | null): void {
    ctx.setWet(null);
    const hasInk = !!wet && wet.polys.length > 0;
    if (!hasInk && !this.erasedAny) {
      ctx.history.abort();
      return;
    }
    if (wet && hasInk) ctx.commitWet(wet);
    ctx.history.commit("Borrar");
  }

  // ------------------------------------------------------- borrado ráster

  /**
   * Goma del pincel ráster: abre el bitmap de la capa de píxeles. Sub-modo
   * `brush` → trazo de borrado en vivo (ancho constante); `shape` → la forma
   * activa borra su área al soltar. Los sub-modos `object`/`color` no aplican a
   * píxeles, así que no hacen nada.
   */
  private beginRasterErase(ctx: ToolContext, s: InputSample): void {
    const em = ctx.brush.eraseMode;
    if (em === "brush") {
      this.rasterErasing = true;
      this.builder = new StrokeBuilder({ ...ctx.brush, dynamics: "constant", minRatio: 1 });
      this.builder.setZoom(ctx.camera.zoom);
      this.builder.begin(this.worldSample(ctx, s));
      this.raster = true;
      this.rasterSent = 0;
      ctx.rasterStrokeBegin(true);
      this.flushRaster(ctx);
      return;
    }
    if (em === "shape") {
      this.rasterErasing = true;
      this.paintWet(ctx);
    }
    // object/color: no se puede seleccionar "objetos" de píxeles; no se borra nada.
  }

  private moveRasterErase(ctx: ToolContext, samples: InputSample[]): void {
    const em = ctx.brush.eraseMode;
    if (em === "brush") {
      const b = this.builder;
      if (!b) return;
      b.setZoom(ctx.camera.zoom);
      if (b.push(samples.map((sm) => this.worldSample(ctx, sm)))) this.flushRaster(ctx);
      return;
    }
    if (em === "shape") {
      const w = ctx.toWorld(samples[samples.length - 1]);
      this.lastX = w.x;
      this.lastY = w.y;
      this.paintWet(ctx);
    }
  }

  private endRasterErase(ctx: ToolContext, s: InputSample): void {
    const em = ctx.brush.eraseMode;
    this.rasterErasing = false;
    if (em === "brush") {
      const b = this.builder;
      this.builder = null;
      if (b) b.push([this.worldSample(ctx, s)], true);
      if (this.raster) {
        this.flushRaster(ctx);
        this.raster = false;
        ctx.rasterStrokeEnd();
      }
      ctx.history.abort();
      return;
    }
    if (em === "shape") {
      const w = ctx.toWorld(s);
      this.lastX = w.x;
      this.lastY = w.y;
      const shape = this.buildEraseShape(ctx);
      ctx.setWet(null);
      ctx.fillRaster([shape], true);
      ctx.history.abort();
      return;
    }
    ctx.history.abort();
  }

  /**
   * Consolida un gesto con "Hacer materia": cada silueta del trazo se convierte
   * en UN cuerpo de forma libre (poly) que conserva el contorno dibujado. Las
   * copias de simetria nacen como cuerpos hermanos; cada uno lleva su pivote en
   * el centroide local de su propia forma.
   */
  private finishMatter(ctx: ToolContext, polys: readonly Polygon[]): void {
    ctx.setWet(null);
    const target = ctx.doc.matterTarget();
    const transforms = symmetryTransforms(ctx.doc.symmetry);
    let created = 0;
    for (const poly of polys) {
      const strokeId = uid();
      for (const m of transforms) {
        const world = transformPolygon(poly, m);
        const body = bodyFromOutline(world, {
          color: ctx.color,
          blend: ctx.doc.field.blend,
          restitution: ctx.doc.physics.settings.restitution,
          friction: ctx.doc.physics.settings.friction,
          layerId: target.id,
        });
        if (!body) continue;
        body.strokeId = strokeId;
        body.strokeIndex = created;
        ctx.doc.physics.add(body);
        created++;
      }
    }
    if (created === 0) {
      ctx.history.abort();
      return;
    }
    ctx.doc.physics.wakeAll();
    ctx.history.commit(created > 1 ? `Materia · ${created} formas` : "Materia");
    ctx.invalidateField();
  }

  /**
   * Consolida un TRAZO con "Acuarela": su linea central (puntos con radio por
   * presion/afilado) se siembra en el plano de fluido, aplicando la simetria
   * activa. El resultado no es tinta vectorial: es pigmento+agua que fluyen. El
   * historial no lo registra (el fluido es un medio en vivo), asi que se aborta.
   */
  private finishAquaStroke(ctx: ToolContext, points: readonly StrokePoint[]): void {
    ctx.setWet(null);
    if (points.length === 0) {
      ctx.history.abort();
      return;
    }
    // Simetria: cada transformacion genera una copia del trazo central.
    const transforms = symmetryTransforms(ctx.doc.symmetry);
    for (const m of transforms) {
      const tp = points.map((p) => {
        const w = { x: m.a * p.x + m.c * p.y + m.e, y: m.b * p.x + m.d * p.y + m.f };
        // El radio escala con la parte lineal de la matriz (det^0.5 aprox).
        const scale = Math.sqrt(Math.abs(m.a * m.d - m.b * m.c)) || 1;
        return { x: w.x, y: w.y, r: p.r * scale, p: p.p, v: p.v, a: p.a, t: p.t };
      });
      ctx.stampAquaStroke(tp, ctx.color);
    }
    ctx.history.abort();
  }

  /**
   * Consolida un RELLENO o FORMA con "Acuarela": las siluetas cerradas se
   * siembran como manchas en el fluido, con la simetria activa.
   */
  private finishAquaArea(ctx: ToolContext, polys: readonly Polygon[]): void {
    ctx.setWet(null);
    if (polys.length === 0) {
      ctx.history.abort();
      return;
    }
    const transforms = symmetryTransforms(ctx.doc.symmetry);
    const stamped: Polygon[] = [];
    for (const poly of polys) {
      for (const m of transforms) stamped.push(transformPolygon(poly, m));
    }
    ctx.stampAquaArea(stamped, ctx.color);
    ctx.history.abort();
  }

  /**
   * Envía al estampador los puntos consolidados del builder que aún no se han
   * estampado. `rasterSent` marca el corte; los puntos del builder son estables
   * una vez empujados, así que basta con mandar la cola nueva.
   */
  private flushRaster(ctx: ToolContext): void {
    const b = this.builder;
    if (!b) return;
    const pts = b.points;
    if (pts.length > this.rasterSent) {
      ctx.rasterStrokeAdd(pts.slice(this.rasterSent));
      this.rasterSent = pts.length;
    }
  }

  /** Elimina los trazos que toca el punto (todo el item, con sus copias). */
  private eraseObjectsAt(ctx: ToolContext, x: number, y: number): void {
    const r = Math.max(1, ctx.brush.size * 0.5);
    const byColor = ctx.brush.eraseMode === "color";
    const remove: string[] = [];
    for (const it of ctx.doc.items) {
      if (it.erase) continue; // no borrar los propios recortes
      if (byColor && !sameColor(it.color, this.eraseRef)) continue;
      if (itemHit(it, x, y, r)) remove.push(it.id);
    }
    for (const id of remove) {
      ctx.doc.removeItem(id);
      this.erasedAny = true;
    }
    if (remove.length > 0) ctx.invalidateInk();
  }

  /** Quita un cuerpo de materia bajo el punto (sub-opcion "incluir materia"). */
  private eraseMatterAt(ctx: ToolContext, x: number, y: number): void {
    const body = ctx.doc.physics.pick(x, y, Math.max(1, ctx.brush.size * 0.3));
    if (!body) return;
    ctx.doc.physics.remove(body.id);
    this.erasedAny = true;
    ctx.invalidateField();
  }

  /** Color del trazo mas alto bajo el punto (referencia del borrado por color). */
  private colorAt(ctx: ToolContext, x: number, y: number): string {
    const r = Math.max(1, ctx.brush.size * 0.5);
    const items = ctx.doc.items;
    for (let i = items.length - 1; i >= 0; i--) {
      const it = items[i];
      if (it.erase) continue;
      if (itemHit(it, x, y, r)) return it.color;
    }
    return ctx.color;
  }

  /** Huella del borrador con forma: la forma activa, dimensionada por arrastre. */
  private buildEraseShape(ctx: ToolContext): Polygon {
    const dx = this.lastX - this.startX;
    const dy = this.lastY - this.startY;
    const d = Math.hypot(dx, dy);
    const size = Math.max(ctx.doc.shape.size, d);
    const angle = d > 2 ? Math.atan2(dy, dx) : 0;
    const local = outlinePolygon({ ...ctx.doc.shape, size });
    const ca = Math.cos(angle);
    const sa = Math.sin(angle);
    return local.map((p) => ({
      x: this.startX + ca * p.x - sa * p.y,
      y: this.startY + sa * p.x + ca * p.y,
    }));
  }

  // ------------------------------------------------------------------ comun

  private finish(ctx: ToolContext, wet: WetStroke | null, label: string): void {
    ctx.setWet(null);
    if (!wet || wet.polys.length === 0) {
      ctx.history.abort();
      return;
    }
    ctx.commitWet(wet);
    ctx.history.commit(label);
  }

  private worldSample(ctx: ToolContext, s: InputSample): WorldSample {
    const w = ctx.toWorld(s);
    return {
      x: w.x,
      y: w.y,
      pressure: s.pressure,
      tilt: s.tilt,
      azimuth: s.azimuth,
      t: s.t,
      predicted: s.predicted,
    };
  }

  private paintWet(ctx: ToolContext): void {
    ctx.setWet(this.buildWet(ctx, true));
  }

  private buildWet(ctx: ToolContext, preview: boolean): WetStroke | null {
    if (ctx.brush.mode === "erase" && ctx.brush.eraseMode === "shape") {
      return this.pack(ctx, [this.buildEraseShape(ctx)]);
    }
    if (ctx.brush.mode === "pull") {
      if (!this.shape) return null;
      const poly = placePullShape(
        this.shape,
        this.startX,
        this.startY,
        this.lastX,
        this.lastY,
        0.5,
        ctx.brush.size,
      );
      return this.pack(ctx, [poly]);
    }
    const b = this.builder;
    if (!b) return null;
    const pts = preview ? b.preview : b.points;
    return this.wetFromPoints(ctx, pts, preview);
  }

  private wetFromPoints(
    ctx: ToolContext,
    points: readonly StrokePoint[],
    _preview: boolean,
  ): WetStroke | null {
    if (points.length === 0) return null;
    const st = ctx.brush;

    if (st.mode === "fill") {
      // El relleno ignora el ancho: importa la trayectoria cerrada.
      if (points.length < 3) return null;
      const poly: Polygon = points.map((p) => ({ x: p.x, y: p.y }));
      return this.pack(ctx, [poly]);
    }

    // El borrador de pincel (o cualquier modo invertido con Alt) usa contorno
    // suave, nunca splat.
    const splat = st.mode === "erase" || this.invert ? false : st.splat;
    const poly = strokeOutline(points, { splat, caps: true });
    if (poly.length < 3) return null;
    return this.pack(ctx, [poly]);
  }

  private pack(ctx: ToolContext, polys: Polygon[]): WetStroke {
    let gy0 = Infinity;
    let gy1 = -Infinity;
    for (const p of polys) {
      const b = polygonBounds(p);
      if (b.y < gy0) gy0 = b.y;
      if (b.y + b.h > gy1) gy1 = b.y + b.h;
    }
    const erasing = ctx.brush.mode === "erase" || this.invert;
    return {
      polys,
      transforms: symmetryTransforms(ctx.doc.symmetry),
      // El borrado no aporta color: el renderer usa alpha (destination-out).
      color: erasing ? "#000000" : this.eraser ? ctx.doc.meta.background : ctx.color,
      opacity: erasing ? (ctx.brush.eraseFade ? ctx.brush.opacity : 1) : ctx.brush.opacity,
      smooth: erasing ? true : !ctx.brush.splat,
      gradient: erasing ? false : ctx.brush.gradient,
      erase: erasing,
      gy0: Number.isFinite(gy0) ? gy0 : 0,
      gy1: Number.isFinite(gy1) ? gy1 : 1,
    };
  }
}

/** Compara dos colores hex sin distinguir mayusculas. */
function sameColor(a: string, b: string): boolean {
  return a.toLowerCase() === b.toLowerCase();
}

/**
 * ¿El punto (con radio `r`) toca la tinta del item? Se prueba cada copia de
 * simetria transformando su poligono a mundo: dentro del relleno, o a menos de
 * `r` de un borde (para trazos finos). El descarte rapido usa la caja del item.
 */
function itemHit(item: InkItem, x: number, y: number, r: number): boolean {
  const b = item.bounds;
  if (x < b.x - r || x > b.x + b.w + r || y < b.y - r || y > b.y + b.h + r) return false;
  for (const m of item.transforms as Mat2d[]) {
    for (const poly of item.polys) {
      if (poly.length < 3) continue;
      const wp = transformPolygon(poly, m);
      if (pointInPoly(wp, x, y)) return true;
      if (r > 0 && nearEdge(wp, x, y, r)) return true;
    }
  }
  return false;
}

/** Punto en poligono por lanzamiento de rayo (regla par/impar). */
function pointInPoly(poly: Polygon, x: number, y: number): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i].x;
    const yi = poly[i].y;
    const xj = poly[j].x;
    const yj = poly[j].y;
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/** ¿Algun borde del poligono queda a distancia <= r del punto? */
function nearEdge(poly: Polygon, x: number, y: number, r: number): boolean {
  const r2 = r * r;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    if (distSqToSeg(x, y, poly[j].x, poly[j].y, poly[i].x, poly[i].y) <= r2) return true;
  }
  return false;
}

/** Distancia al cuadrado del punto (px,py) al segmento (ax,ay)-(bx,by). */
function distSqToSeg(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax;
  const dy = by - ay;
  const l2 = dx * dx + dy * dy;
  let t = l2 > 0 ? ((px - ax) * dx + (py - ay) * dy) / l2 : 0;
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  const ex = px - (ax + t * dx);
  const ey = py - (ay + t * dy);
  return ex * ex + ey * ey;
}
